import React, { useRef, useState } from 'react';
import VimeoEmbed from '../VimeoEmbed';

const DEFAULT_VIDEO_WIDTH = 1067;

const VideoPreviewModal = ({
    open,
    close,
    title,
    videoId,
    autoplay = false,
    onEnded,
}: {
    open: boolean;
    close: () => void;
    title: string;
    videoId: string;
    autoplay?: boolean;
    onEnded?: () => void;
}) => {
    const headerHeight = 42;
    const windowRef = useRef<HTMLElement | null>(null);
    const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);
    const resizeRef = useRef<{ startX: number; startY: number; width: number } | null>(null);
    const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
    const [width, setWidth] = useState(() => {
        if (typeof window === 'undefined') return DEFAULT_VIDEO_WIDTH;
        return Math.min(DEFAULT_VIDEO_WIDTH, window.innerWidth - 32, (window.innerHeight - 32 - headerHeight) * (16 / 9));
    });
    const height = headerHeight + width * (9 / 16);

    if (!open) return null;

    const handleDragStart = (event: React.PointerEvent<HTMLElement>) => {
        if ((event.target as HTMLElement).closest('button')) return;

        const rect = windowRef.current?.getBoundingClientRect();
        if (!rect) return;

        dragRef.current = {
            offsetX: event.clientX - rect.left,
            offsetY: event.clientY - rect.top,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleDragMove = (event: React.PointerEvent<HTMLElement>) => {
        if (!dragRef.current) return;

        const maxLeft = Math.max(8, window.innerWidth - width - 8);
        const maxTop = Math.max(8, window.innerHeight - height - 8);
        setPosition({
            left: Math.max(8, Math.min(event.clientX - dragRef.current.offsetX, maxLeft)),
            top: Math.max(8, Math.min(event.clientY - dragRef.current.offsetY, maxTop)),
        });
    };

    const handleDragEnd = () => {
        dragRef.current = null;
    };

    const handleResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        resizeRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            width,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handleResizeMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!resizeRef.current) return;

        const { startX, startY, width: startWidth } = resizeRef.current;
        const rect = windowRef.current?.getBoundingClientRect();
        const maxWidthByViewport = window.innerWidth - (rect?.left ?? 8) - 8;
        const maxWidthByHeight = (window.innerHeight - (rect?.top ?? 8) - 8 - headerHeight) * (16 / 9);
        const maxWidth = Math.max(320, Math.min(maxWidthByViewport, maxWidthByHeight));
        const widthFromX = startWidth + event.clientX - startX;
        const widthFromY = startWidth + (event.clientY - startY) * (16 / 9);
        const nextWidth = Math.abs(event.clientX - startX) >= Math.abs(event.clientY - startY)
            ? widthFromX
            : widthFromY;

        setWidth(Math.max(320, Math.min(nextWidth, maxWidth)));
    };

    const handleResizeEnd = () => {
        resizeRef.current = null;
    };

    return (
        <section
            ref={windowRef}
            className="video-preview-floating-window"
            role="dialog"
            aria-modal="false"
            aria-label={title}
            style={{
                width,
                height,
                ...(position
                    ? {
                        left: position.left,
                        top: position.top,
                        right: 'auto',
                        bottom: 'auto',
                        transform: 'none',
                    }
                    : {
                        left: '50%',
                        top: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        transform: 'translate(-50%, -50%)',
                    }),
            }}
        >
            <header
                className="video-preview-floating-window__header"
                onPointerDown={handleDragStart}
                onPointerMove={handleDragMove}
                onPointerUp={handleDragEnd}
                onPointerCancel={handleDragEnd}
            >
                <span className="text-truncate">{title}</span>
                <button
                    type="button"
                    className="video-preview-floating-window__close"
                    onClick={close}
                    aria-label="Close video"
                >
                    <i className="fa-light fa-xmark" aria-hidden="true" />
                </button>
            </header>
            <div className="video-preview-floating-window__player">
                <VimeoEmbed key={videoId} url={videoId} fill autoplay={autoplay} onEnded={onEnded} />
            </div>
            <div
                className="video-preview-floating-window__resize-grip"
                role="presentation"
                onPointerDown={handleResizeStart}
                onPointerMove={handleResizeMove}
                onPointerUp={handleResizeEnd}
                onPointerCancel={handleResizeEnd}
            />
        </section>
    );
};
export default VideoPreviewModal;
